/**
 * Account — the user's own corner: WhatsApp notification preferences,
 * invoices (one per successful charge, viewable/printable), and "My requests"
 * — every support/change request they filed with the full reply thread.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
import { useCallback, useEffect, useState } from 'react';
import AppNav from '../components/AppNav.jsx';
import Section from '../components/Section.jsx';
import { Link } from 'react-router-dom';
import { api, tokens } from '../api.js';
// Vault's own prices and revenue are rand (Paystack), whatever the account's currency.
import { fmtZAR as fmtR } from '../money.js';
import { ask } from '../components/Ask.jsx';
import { useAuth } from '../App.jsx';
import { AIDisclosure, useAIConsent } from '../components/AIGate.jsx';
import { SimpleViewSetting } from '../components/simple/viewMode.jsx';
import RegionSettings from '../components/RegionSettings.jsx';

export default function Account() {
  const { me, reload, logout } = useAuth();
  const aiConsent = useAIConsent();
  // Linked from the "AI couldn't run" notice: open the AI section and bring it into view.
  const aiLinked = typeof window !== 'undefined' && window.location.hash === '#ai-processing';
  useEffect(() => {
    if (aiLinked) setTimeout(() => document.getElementById('ai-processing')?.scrollIntoView({ block: 'start' }), 80);
  }, [aiLinked]);
  const [invoices, setInvoices] = useState([]);
  const [plans, setPlans] = useState(null);   // billing prices + yearly availability
  const [requests, setRequests] = useState([]);
  const [saved, setSaved] = useState('');
  const [watchCode, setWatchCode] = useState(null);
  const [codeBusy, setCodeBusy] = useState(false);
  const [delOpen, setDelOpen] = useState(false);   // POPIA delete confirm panel
  const [delVal, setDelVal] = useState('');
  const [delBusy, setDelBusy] = useState(false);
  const [delErr, setDelErr] = useState('');
  const isSocial = !!(me?.auth_provider && me.auth_provider !== 'email');

  // Referral share link + a prefilled WhatsApp message (meaningful once
  // /auth/me has returned a referral_code).
  const refLink = me?.referral_code
    ? `${window.location.origin}/?ref=${me.referral_code}` : '';
  const waHref = `https://wa.me/?text=${encodeURIComponent(
    `I'm using Vault to stay on top of my money - join with my link and we both get extra time to try it free: ${refLink}`,
  )}`;

  const load = useCallback(() => {
    api('/billing/invoices/').then(setInvoices).catch(() => {});
    api('/feedback/journeys/mine/').then(setRequests).catch(() => {});
    api('/billing/status/').then(setPlans).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  // POPIA data export — every owned row as one JSON file, downloaded in-browser.
  const exportData = async () => {
    try {
      const data = await api('/auth/export/');
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'vault-data-export.json';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      ask({ title: 'Export failed', message: e.data?.detail || e.message || 'Try again in a moment.', ok: 'OK' });
    }
  };
  // POPIA: the CSV export and the receipts zip are streamed behind the JWT
  // (never a public URL), so fetch them as an authenticated blob and save it.
  const downloadAuthed = async (path, filename, failTitle) => {
    try {
      const r = await fetch(`/api${path}`, { headers: { Authorization: `Bearer ${tokens.access}` } });
      if (!r.ok) throw new Error('download failed');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      ask({ title: failTitle, message: e.message || 'Try again in a moment.', ok: 'OK' });
    }
  };
  const exportCsv = () => downloadAuthed('/auth/export/csv/', 'vault-data-export-csv.zip', 'CSV export failed');
  const downloadReceipts = () => downloadAuthed('/auth/receipts/', 'vault-receipts.zip', 'Receipts download failed');
  // POPIA account deletion — password (email) or the word DELETE (social), then
  // the account and every owned row are erased and the session is cleared.
  const doDelete = async () => {
    setDelBusy(true); setDelErr('');
    try {
      await api('/auth/delete/', { method: 'POST', body: isSocial ? { confirm: delVal.trim() } : { password: delVal } });
      try { tokens.clear(); } catch { /* logging out regardless */ }
      window.location.href = '/';
    } catch (e) {
      setDelErr(e.data?.detail || 'Could not delete — check and try again.'); setDelBusy(false);
    }
  };

  const savePrefs = async (body) => {
    await api('/auth/me/', { method: 'PATCH', body });
    setSaved('Saved ✓'); setTimeout(() => setSaved(''), 2000);
    reload();
  };

  const uploadAvatar = async (f) => {
    if (!f) return;
    const fd = new FormData();
    fd.append('avatar', f);
    await api('/auth/me/', { method: 'PATCH', form: fd });
    setSaved('Photo updated ✓'); setTimeout(() => setSaved(''), 2000);
    reload();
  };

  // Choose monthly vs yearly BEFORE billing starts (trial / past due). Records
  // the preference; the trial sweep or the paywall's Subscribe then bills that
  // plan. Not offered once a subscription is live (that's a cancel + resubscribe).
  const setPlan = async (interval) => {
    if (me?.billing_interval === interval) return;
    try {
      await api('/billing/interval/', { method: 'POST', body: { interval } });
      setSaved(interval === 'yearly' ? 'Switched to yearly ✓' : 'Switched to monthly ✓');
      setTimeout(() => setSaved(''), 3000);
      reload();
    } catch (e) { setSaved(e.data?.detail || 'Could not change plan — try again.'); }
  };

  // Restart after a cancel — resumes the same subscription when it still exists
  // (no new charge), otherwise starts a fresh one on the card on file.
  const restartSub = async () => {
    try {
      await api('/billing/subscribe/', { method: 'POST' });
      setSaved('Subscription restarted ✓'); setTimeout(() => setSaved(''), 3000);
      reload();
    } catch (e) { setSaved(e.data?.detail || 'Could not restart — try again.'); }
  };

  const cancelSub = async () => {
    if (!await ask({
      title: 'End your Vault subscription?',
      message: 'Paystack stops charging and access lasts until the period you '
        + 'already paid for runs out. Your data stays exportable and deletable.',
      ok: 'End subscription', danger: true, cancel: 'Stay',
    })) return;
    try {
      await api('/billing/cancel/', { method: 'POST' });
      setSaved('Subscription ended ✓'); setTimeout(() => setSaved(''), 3000);
      reload();
    } catch (e) { setSaved(e.data?.detail || 'Could not cancel — try again.'); }
  };

  // Invoices are behind the JWT — fetch as a blob and open the printable page
  // in a new tab (same pattern as receipts; no tokens in URLs).
  const openInvoice = async (inv) => {
    const r = await fetch(`/api/billing/invoices/${inv.id}/`,
                          { headers: { Authorization: `Bearer ${tokens.access}` } });
    const blob = await r.blob();
    window.open(URL.createObjectURL(blob.slice(0, blob.size, 'text/html')), '_blank');
  };

  return (
    <div className="min-h-screen bg-ink text-slate-100">
      <AppNav active={null} />
      <div className="w-full max-w-3xl mx-auto px-5 md:px-8 py-6">
      <div className="flex items-center gap-3 mb-5">
        <h1 className="text-2xl font-bold flex-1">Account</h1>
        <span className="text-sm text-slate-500 truncate">{me?.email}</span>
      </div>
      {saved && <p className="text-emerald-400 text-sm mb-3">{saved}</p>}

      {/* #476 — Simple view is the first row of Account. */}
      <div className="sv-scope mb-4"><SimpleViewSetting /></div>

      <Section title="Profile" icon="👤" subtitle="Name & photo" defaultOpen>
        <div className="mt-1 flex items-start gap-4 flex-wrap">
          <Avatar hasAvatar={!!me?.avatar} />
          <div className="flex-1 min-w-[220px]">
            <div className="flex gap-2">
              <input className="input" placeholder="First name" defaultValue={me?.first_name || ''}
                     onBlur={(e) => e.target.value !== (me?.first_name || '')
                                    && savePrefs({ first_name: e.target.value.trim() })} />
              <input className="input" placeholder="Last name" defaultValue={me?.last_name || ''}
                     onBlur={(e) => e.target.value !== (me?.last_name || '')
                                    && savePrefs({ last_name: e.target.value.trim() })} />
            </div>
            <div className="flex gap-2 mt-2 text-sm">
              <label className="border border-edge rounded-xl px-3 py-2 cursor-pointer hover:border-vblue transition">
                📷 Camera
                <input type="file" accept="image/*" capture="user" className="hidden"
                       onChange={(e) => uploadAvatar(e.target.files?.[0])} />
              </label>
              <label className="border border-edge rounded-xl px-3 py-2 cursor-pointer hover:border-vblue transition">
                🖼 Photo
                <input type="file" accept="image/*" className="hidden"
                       onChange={(e) => uploadAvatar(e.target.files?.[0])} />
              </label>
            </div>
          </div>
        </div>
      </Section>

      {/* Going global: where the member lives and which currency their money is in. */}
      <Section title="Country & currency" icon="🌍" dataGuide="account-region"
               subtitle={`${me?.country || 'ZA'} · ${me?.home_currency || 'ZAR'}`}>
        <RegionSettings />
      </Section>

      {/* AI processing — the permission Apple 5.1.1(i) requires, and the way to
          take it back. Withdrawing stops every AI call at the server gate. */}
      <Section id="ai-processing" title="AI processing" icon="✨" defaultOpen={aiLinked}
               subtitle={me?.ai_consent_required === true ? 'Not allowed yet' : 'Allowed'}>
        <p className="text-sm text-slate-400 mt-1 mb-3">
          {me?.ai_consent_required === true
            ? 'Allow it below, or Vault will ask the first time you use an AI feature. Everything else works as normal.'
            : 'You can withdraw this at any time; every AI feature has a manual alternative.'}
        </p>
        <AIDisclosure />
        {me?.ai_consent_required === true ? (
          // Allow it here too — before, the only way in was an AI feature asking,
          // and one that didn't ask (statement import) left no way to switch it on.
          <button type="button" className="btn-primary mt-4"
                  onClick={() => aiConsent.allow(true)}>Allow AI processing</button>
        ) : (
          <button type="button" className="border border-edge rounded-xl px-4 text-sm text-slate-200 hover:border-vblue transition py-2.5 mt-4"
                  onClick={() => aiConsent.allow(false)}>Withdraw permission</button>
        )}
      </Section>

      <Section title="WhatsApp notifications" icon="💬" dataGuide="account-whatsapp"
               subtitle={me?.whatsapp_optin ? 'On' : 'Off - support replies & nudges'}>
        <p className="text-sm text-slate-400 mt-1">
          Optional: support replies and helpful nudges from Vault's AI on WhatsApp.
          Off by default - nothing sends unless you switch it on.
        </p>
        <div className="mt-3 flex gap-2 items-center flex-wrap">
          <input className="input !w-56" placeholder="+27 82 000 0000"
                 defaultValue={me?.whatsapp_number || ''}
                 onBlur={(e) => e.target.value !== (me?.whatsapp_number || '')
                                && savePrefs({ whatsapp_number: e.target.value.trim() })} />
          <button className={`px-4 py-2 rounded-xl border text-sm transition ${
                    me?.whatsapp_optin ? 'border-emerald-400 text-emerald-400'
                                       : 'border-edge text-slate-400 hover:border-slate-500'}`}
                  onClick={() => savePrefs({ whatsapp_optin: !me?.whatsapp_optin })}>
            {me?.whatsapp_optin ? 'On - tap to switch off' : 'Off - tap to switch on'}
          </button>
        </div>
      </Section>

      <Section title="Link my watch" icon="⌚" dataGuide="account-watch"
               subtitle="Pair Apple Watch / Wear OS with a code">
        <p className="text-sm text-slate-400 mt-1">
          Pair Vault on your Apple Watch or Wear OS watch without typing your password.
          Tap Generate, then on the watch tap "Link with a phone code" and enter this code.
        </p>
        {watchCode ? (
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <span className="text-3xl font-extrabold tracking-widest tabular-nums text-vblue">{watchCode}</span>
            <span className="text-xs text-slate-500">Valid 10 minutes - single use</span>
            <button className="text-xs underline text-slate-400" onClick={() => setWatchCode(null)}>Done</button>
          </div>
        ) : (
          <button className="btn-primary !py-2 mt-3" disabled={codeBusy}
                  onClick={async () => {
                    setCodeBusy(true);
                    try {
                      const d = await api('/auth/watch/link-code/', { method: 'POST' });
                      if (d?.code) setWatchCode(d.code);
                    } catch { /* ignore */ } finally { setCodeBusy(false); }
                  }}>
            {codeBusy ? 'Generating...' : 'Generate watch code'}
          </button>
        )}
      </Section>

      {me?.referral_code && (
        <Section title="Invite friends" icon="🎁" dataGuide="account-invite"
                 subtitle={`${me.referral_count || 0} joined - ${me.referral_bonus_days || 0} bonus days`}>
          <p className="text-sm text-slate-400 mt-1">
            Share your link on WhatsApp. Every friend who signs up gives you{' '}
            {me.referral_bonus_per_referral || 6} extra free days (up to{' '}
            {me.referral_max_bonus_days || 30} in total) - and they get{' '}
            {me.referral_welcome_days || 5} welcome days too, so you both win.
          </p>
          <div className="mt-3 flex gap-2 items-center flex-wrap">
            <input className="input flex-1 !min-w-0" readOnly value={refLink}
                   onFocus={(e) => e.target.select()} />
            <button className="px-4 py-2 rounded-xl border border-edge text-sm text-slate-300 hover:border-vblue transition"
                    onClick={() => {
                      try { navigator.clipboard?.writeText(refLink); } catch { /* ignore */ }
                      setSaved('Link copied'); setTimeout(() => setSaved(''), 2000);
                    }}>
              Copy
            </button>
          </div>
          <a href={waHref} target="_blank" rel="noreferrer"
             className="btn-primary !py-2 mt-3 inline-block">Share on WhatsApp</a>
        </Section>
      )}

      <Section title="Invoices" icon="🧾" subtitle={invoices.length ? `${invoices.length} on file` : 'None yet'}>
        <p className="text-sm text-slate-400 mt-1">One per successful charge. Open, then print or save as PDF.</p>
        <div className="mt-3 grid gap-2">
          {invoices.map((inv) => (
            <button key={inv.id}
                    className="flex items-center gap-3 border border-edge rounded-xl px-4 py-2.5 text-left hover:border-vblue transition text-sm"
                    onClick={() => openInvoice(inv)}>
              <span className="font-medium flex-1">{inv.number}</span>
              <span className="text-slate-500">{inv.date}</span>
              <span className="font-semibold">R {inv.amount}</span>
            </button>
          ))}
          {!invoices.length && (
            <p className="text-sm text-slate-600">No charges yet - your first invoice appears when your trial converts.</p>
          )}
        </div>
      </Section>

      {(() => {
        const monthly = plans?.price_monthly_zar ?? 59;
        const yearly = plans?.price_yearly_zar ?? 590;
        const yearlyOn = !!plans?.yearly_available;
        const cur = me?.billing_interval === 'yearly' ? 'yearly' : 'monthly';
        const active = me?.subscription_status === 'active';
        const cancelled = me?.subscription_status === 'cancelled';
        const trialing = !active && !cancelled;
        const planLabel = cur === 'yearly' ? `${fmtR(yearly)}/year` : `${fmtR(monthly)}/month`;
        const saveZar = Math.max(0, monthly * 12 - yearly);
        return (
      <Section title="Subscription" icon="💳"
               subtitle={cancelled ? 'Cancelled' : `${planLabel} via Paystack`}>
        <p className="text-sm text-slate-400 mt-1">
          {cancelled
            ? 'Cancelled - no further charges. Access lasts until your paid period ends.'
            : `${planLabel} via Paystack (appears as "Mare Greyling" on your statement).`}
        </p>

        {yearlyOn && (
          <>
            <div className="mt-3 grid grid-cols-2 gap-2">
              {[['monthly', fmtR(monthly), 'per month'],
                ['yearly', fmtR(yearly), 'per year']].map(([k, big, sub]) => (
                <button key={k} type="button" onClick={() => setPlan(k)}
                        className={`rounded-xl border p-3 text-left transition ${
                          cur === k ? 'border-vblue bg-vblue/10' : 'border-edge hover:border-slate-500'}`}>
                  <div className="text-base font-bold">{big}</div>
                  <div className="text-xs text-slate-400">{sub}</div>
                  {k === 'yearly' && saveZar > 0 && (
                    <div className="text-[11px] text-emerald-400 mt-1">Save {fmtR(saveZar)} — 2 months free</div>
                  )}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-slate-500 mt-2">
              {active
                ? 'Switching plan takes effect at your next renewal — no charge today, and no loss of access.'
                : cancelled
                  ? 'Pick the plan you want, then restart below.'
                  : 'Your pick is what you will be charged when your trial ends — change it any time before then.'}
            </p>
          </>
        )}

        {cancelled ? (
          <button className="btn-primary mt-3" onClick={restartSub}>
            Restart subscription
          </button>
        ) : trialing ? (
          <Link to="/paywall" className="btn-primary mt-3 inline-block">
            Subscribe now
          </Link>
        ) : (
          <button className="mt-3 text-sm border border-edge rounded-xl px-4 py-2 text-red-400 hover:border-red-400 transition"
                  onClick={cancelSub}>
            End subscription
          </button>
        )}
        <p className="text-xs text-slate-600 mt-2">
          Cancelling never touches your data - export (free) and delete stay available
          under <Link to="/privacy" className="underline">POPIA</Link>.
        </p>
      </Section>
        );
      })()}

      <Section title="Your data" icon="🔒" dataGuide="account-data"
               subtitle="Export or delete everything — free, under POPIA">
        <p className="text-sm text-slate-400 mt-1">
          Your data is yours. Download every record as JSON or CSV, pull all your
          receipts as a zip, or permanently delete your account and everything it
          owns — all free, unlimited, no support ticket. Delete cannot be undone.
        </p>
        {me?.consent_at && (
          <p className="text-[11px] text-slate-500 mt-2">
            You agreed to our <Link to="/terms" className="underline">terms</Link> and{' '}
            <Link to="/privacy" className="underline">privacy notice</Link>
            {me.consent_notice_version ? ` (v${me.consent_notice_version})` : ''} on{' '}
            {new Date(me.consent_at).toLocaleDateString()}.
          </p>
        )}
        <div className="flex flex-wrap gap-2 mt-3">
          <button className="text-sm border border-edge rounded-xl px-4 py-2 text-slate-200 hover:border-vblue transition"
                  onClick={exportData}>⬇ Export (JSON)</button>
          <button className="text-sm border border-edge rounded-xl px-4 py-2 text-slate-200 hover:border-vblue transition"
                  onClick={exportCsv}>⬇ Export (CSV)</button>
          <button className="text-sm border border-edge rounded-xl px-4 py-2 text-slate-200 hover:border-vblue transition"
                  onClick={downloadReceipts}>🧾 All receipts (zip)</button>
          {!delOpen && (
            <button className="text-sm border border-edge rounded-xl px-4 py-2 text-red-400 hover:border-red-400 transition"
                    onClick={() => { setDelOpen(true); setDelVal(''); setDelErr(''); }}>Delete my account</button>
          )}
        </div>
        {delOpen && (
          <div className="mt-3 border border-red-400/40 rounded-xl p-4 bg-red-400/[0.04]">
            <div className="text-sm font-semibold text-red-300">Delete your account permanently?</div>
            <p className="text-xs text-slate-400 mt-1">
              This erases every Vault record you own and ends any subscription. It cannot be undone.
            </p>
            <input type={isSocial ? 'text' : 'password'} className="input mt-3" value={delVal} autoComplete="off"
                   onChange={(e) => setDelVal(e.target.value)}
                   placeholder={isSocial ? 'Type DELETE to confirm' : 'Enter your password'} />
            {delErr && <p className="text-xs text-red-400 mt-1">{delErr}</p>}
            <div className="flex gap-2 mt-3">
              <button className="text-sm border border-red-400/50 text-red-400 rounded-xl px-4 py-2 hover:bg-red-400/10 transition disabled:opacity-40"
                      disabled={delBusy || !delVal.trim()} onClick={doDelete}>{delBusy ? 'Deleting…' : 'Delete forever'}</button>
              <button className="text-sm border border-edge text-slate-300 rounded-xl px-4 py-2 hover:border-slate-500 transition"
                      onClick={() => setDelOpen(false)}>Cancel</button>
            </div>
          </div>
        )}
      </Section>

      <Section title="My requests" icon="📨" dataGuide="account-requests"
               subtitle={requests.length ? `${requests.length} filed` : 'None yet'}>
        <p className="text-sm text-slate-400 mt-1">Every problem or change request you sent, with every answer.</p>
        <div className="mt-3 grid gap-3">
          {requests.map((r) => (
            <div key={r.id} className="border border-edge rounded-xl p-4">
              <div className="flex items-center gap-2 flex-wrap text-xs">
                <span className={`uppercase tracking-wide rounded px-1.5 py-0.5 border ${
                  r.kind === 'problem' ? 'border-red-400/50 text-red-300'
                                       : 'border-vblue/50 text-vblue'}`}>
                  {r.kind_display}
                </span>
                <span className="text-slate-500">#{r.id} - {r.status_display}</span>
                <span className="text-slate-600">{new Date(r.created_at).toLocaleDateString()}</span>
              </div>
              <p className="text-sm mt-2">{r.title || r.description || '(no description)'}</p>
              {(r.responses || []).map((resp, i) => (
                <div key={i} className="mt-2 border-l-2 border-vpurple/60 pl-3">
                  <p className="text-sm text-slate-300">{resp.body}</p>
                  <p className="text-[11px] text-slate-600 mt-0.5">Vault support - {new Date(resp.at).toLocaleString()}</p>
                </div>
              ))}
              {!(r.responses || []).length && (
                <p className="text-xs text-slate-600 mt-2">No reply yet - we're on it.</p>
              )}
            </div>
          ))}
          {!requests.length && (
            <p className="text-sm text-slate-600">Nothing filed yet - the ? bubble (bottom-left) sends a screenshot report any time.</p>
          )}
        </div>
      </Section>

      {/* Sign out — the app had logout logic (useAuth) but no visible control,
          so a signed-in user could not leave. One clear button, >=44px. */}
      <div className="mt-6 flex justify-center">
        <button type="button"
                onClick={() => { logout(); window.location.assign('/login'); }}
                className="min-h-[44px] px-6 rounded-xl border border-edge text-sm font-medium text-slate-300 hover:bg-edge/40 transition">
          Sign out
        </button>
      </div>
      </div>
    </div>
  );
}

/** The profile photo, fetched with the JWT (nginx denies /media/ directly). */
function Avatar({ hasAvatar }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!hasAvatar) { setUrl(null); return; }
    fetch('/api/auth/me/avatar/', { headers: { Authorization: `Bearer ${tokens.access}` } })
      .then((r) => (r.ok ? r.blob() : null))
      .then((b) => b && setUrl(URL.createObjectURL(b)))
      .catch(() => {});
  }, [hasAvatar]);
  return url
    ? <img src={url} alt="" className="w-20 h-20 rounded-full object-cover border border-edge" />
    : <div className="w-20 h-20 rounded-full border border-edge grid place-items-center text-3xl">👤</div>;
}
